import { useMemo } from 'react'
import { Clock } from 'lucide-react'
import { useCountdown } from '@/hooks/useCountdown'
import { cn } from '@/lib/utils'
import { getUrgencyClass } from './utils'

interface TickCountdownProps {
  /** Whether tick is currently processing */
  isProcessing?: boolean
  /** Additional CSS classes */
  className?: string
  /** Show icon before countdown */
  showIcon?: boolean
  /** Compact mode (no label) */
  compact?: boolean
}

/**
 * Countdown timer to next tick (00:00 UTC daily).
 *
 * Updates every second and shows urgency styling when time is running low.
 */
export function TickCountdown({
  isProcessing = false,
  className,
  showIcon = true,
  compact = false,
}: TickCountdownProps) {
  // Calculate target time: next 00:00 UTC
  const targetTime = useMemo(() => {
    const now = new Date()
    const next = new Date(now)
    next.setUTCHours(24, 0, 0, 0)
    return next
  }, [])

  // Get countdown string from hook
  const countdown = useCountdown(targetTime, isProcessing)

  // Parse hours from countdown for urgency styling
  const hours = useMemo(() => {
    if (isProcessing || countdown === '--:--:--' || countdown === 'Starting soon...') {
      return 24 // Default to no urgency
    }
    const parts = countdown.split(':')
    return parseInt(parts[0], 10)
  }, [countdown, isProcessing])

  // Determine urgency class based on hours remaining
  const urgencyClass = getUrgencyClass(hours)

  return (
    <div className={cn('flex items-center gap-1.5', className)}>
      {showIcon && (
        <Clock
          className={cn(
            'h-4 w-4',
            urgencyClass,
            hours < 1 && 'animate-pulse'
          )}
        />
      )}
      <span
        className={cn(
          'font-mono text-sm font-medium tabular-nums',
          urgencyClass
        )}
        aria-label={`Time until next tick: ${countdown}`}
        role="timer"
      >
        {countdown}
      </span>
      {!compact && (
        <span className="text-xs text-muted-foreground hidden sm:inline">
          until tick
        </span>
      )}
    </div>
  )
}
